import * as Headless from '@headlessui/react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { StatusEnum } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Badge } from '../../../components/badge';
import { Button } from '../../../components/button';
import { ConfirmationDialog } from '../../../components/confirmation-dialog';
import {
  DocumentActionDialogs,
  menuButtonClasses,
  menuPanelClasses,
  useDocumentActions,
} from '../../../components/document-actions';
import {
  DocumentLink,
  DocumentMenu,
  useCanDownloadDocument,
} from '../../../components/document-type-access';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/dropdown';
import { ExpirationBadge } from '../../../components/expiration-badge';
import { WorkflowScopes } from '../../../constants/workflows';
import { useHasPermission } from '../../../hooks/useHasPermission';
import { SIGNATURE_REQUEST_UPDATE_PERMISSION } from '../../../utils/permissions';
import { useAudience } from '../../../utils/workflowAccess';
import { CredentialFormDialog } from './CredentialFormDialog';
import {
  credentialApi,
  CredentialKind,
  CredentialRow,
  CredentialSectionSpec,
  credentialsQueryKey,
  isBoardRow,
  isEcfmgRow,
  isImmunizationRow,
  isLicenseRow,
  rowInSection,
  STAFF_PROVIDER_CREDENTIALS_QUERY_KEY,
} from './credentials';
import {
  formatProviderDate,
  formatProviderMonthYear,
  labelFor,
  labelWithoutCode,
  ProviderDrawerMaps,
  textOrDash,
} from './providerDisplay';
import {
  EditPencil,
  ExpirationDate,
  FieldSpec,
  PROVIDER_UPDATE_PERMISSION,
  RecordCard,
} from './ProviderDrawerPrimitives';
import { STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, STAFF_PROVIDER_QUERY_KEY } from './queryKeys';
import type { SectionItem, Selection } from './SectionList';

const LicenseStatusBadge: React.FC<{ status: string; map?: Record<string, string> }> = ({
  status,
  map,
}) => (
  <Badge color={status === StatusEnum.ACTIVE ? 'green' : 'zinc'}>{labelFor(status, map)}</Badge>
);

// licenses by state (the state IS the identity), immunizations and ECFMG certificates by
// issue date (the latest last), the rest by type code
const sortKey = (row: CredentialRow): string => {
  if (isLicenseRow(row)) return row.state || '';
  if (isImmunizationRow(row) || isEcfmgRow(row)) return row.issue_date || '';
  return row.type || '';
};

const sortRows = (rows: CredentialRow[]): CredentialRow[] =>
  [...rows].sort((a, b) => sortKey(a).localeCompare(sortKey(b)));

const rowBadges = (row: CredentialRow, maps: ProviderDrawerMaps | undefined): React.ReactNode[] => {
  const badges: React.ReactNode[] = [];
  if (isLicenseRow(row)) {
    badges.push(<LicenseStatusBadge key="status" status={row.status} map={maps?.LicenseStatus} />);
  }
  if (isBoardRow(row) && row.board_eligible) {
    badges.push(
      <Badge key="eligible" color="sky">
        Board eligible
      </Badge>
    );
  }
  if (isImmunizationRow(row) && row.exempt) {
    badges.push(
      <Badge key="exempt" color="amber">
        Exempt
      </Badge>
    );
  }
  return badges;
};

const rowFields = (row: CredentialRow, maps: ProviderDrawerMaps | undefined): FieldSpec[] => {
  if (isLicenseRow(row) || isEcfmgRow(row)) {
    return [{ label: 'Number', value: textOrDash(row.number) }];
  }
  // an immunization card's title already names the type and month ("COVID-19 Jun 2024")
  if (isImmunizationRow(row)) return [];
  return [
    {
      label: isBoardRow(row) ? 'Board' : 'Type',
      value: labelWithoutCode(
        row.type,
        isBoardRow(row) ? maps?.BoardCertificationType : maps?.CertificationType
      ),
      stackLabel: true,
    },
  ];
};

// Every immunization entry of a type would otherwise be titled identically ("TB
// Immunization" under the "TB Immunizations" section), so the card says the type and
// the month it was given: "Tuberculosis Jan 2026". The entry's `title` stays the
// document label and the toast / dialog wording.
const cardTitle = (row: CredentialRow, maps: ProviderDrawerMaps | undefined): string =>
  isImmunizationRow(row)
    ? [labelFor(row.type, maps?.ImmunizationType), formatProviderMonthYear(row.issue_date)]
        .filter(Boolean)
        .join(' ')
    : row.title;

type CardProps = {
  row: CredentialRow;
  section: CredentialSectionSpec;
  maps: ProviderDrawerMaps | undefined;
  viewer: Parameters<typeof DocumentLink>[0]['viewer'];
  documentActions: ReturnType<typeof useDocumentActions>;
  canUpdate: boolean;
  leading: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
  testIdPrefix: string;
};

/**
 * One credential entry: title (pencil to edit), badges + expiry badge, the kind's own
 * field, dates, and its document — a view link with the paperclip menu, or, with none on
 * file, an amber note that IS the link to attach one (the edit dialog's file field).
 */
function CredentialEntryCard({
  row,
  section,
  maps,
  viewer,
  documentActions,
  canUpdate,
  leading,
  onEdit,
  onDelete,
  testIdPrefix,
}: CardProps) {
  const document = row.document ?? null;
  const fields: FieldSpec[] = [
    ...rowFields(row, maps),
    { label: 'Issued', value: formatProviderDate(row.issue_date) },
    { label: 'Expires', value: <ExpirationDate value={row.expiration_date} showRelative /> },
    {
      label: 'Document',
      value: document ? (
        <DocumentLink
          doc={document}
          viewer={viewer}
          actions={documentActions}
          menu={false}
          text={document.original_filename}
          data-testid={`${testIdPrefix}-document-${row.id}`}
        />
      ) : (
        <span className="tw:text-amber-700" data-testid={`${testIdPrefix}-no-document-${row.id}`}>
          <i className="fas fa-triangle-exclamation tw:mr-1" aria-hidden="true" />
          {canUpdate ? (
            <button
              type="button"
              className="tw:text-amber-700 tw:underline tw:hover:text-brand-green-0 tw:cursor-pointer tw:bg-transparent tw:border-0 tw:p-0"
              onClick={onEdit}
              title={`Attach a document to ${row.title}`}
              data-testid={`${testIdPrefix}-attach-${row.id}`}
            >
              No document on file — attach one
            </button>
          ) : (
            'No document on file'
          )}
          {row.legacy_file_url && (
            <>
              {' · '}
              <a
                href={row.legacy_file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="tw:text-brand-green-0 tw:hover:underline"
                data-testid={`${testIdPrefix}-legacy-file-${row.id}`}
              >
                legacy file
              </a>
            </>
          )}
        </span>
      ),
    },
  ];
  return (
    <RecordCard
      title={
        <span className="tw:flex tw:items-center tw:gap-2">
          {cardTitle(row, maps)}
          {canUpdate && (
            <EditPencil
              onClick={onEdit}
              title={document ? 'Edit entry' : 'Edit entry / attach document'}
              aria-label={`Edit ${row.title}`}
              data-testid={`${testIdPrefix}-edit-${row.id}`}
            />
          )}
        </span>
      }
      leading={leading}
      rightElement={
        <span className="tw:flex tw:items-center tw:gap-2">
          {rowBadges(row, maps)}
          <ExpirationBadge
            expiresOn={row.expiration_date}
            data-testid={`${testIdPrefix}-${row.id}-expiration`}
          />
        </span>
      }
      fields={fields}
      footer={
        <>
          {document && <DocumentMenu doc={document} viewer={viewer} actions={documentActions} />}
          {canUpdate && (
            <Dropdown>
              <DropdownButton
                as="button"
                className={menuButtonClasses}
                aria-label={`Actions for ${row.title}`}
                title="Entry actions"
                data-testid={`${testIdPrefix}-menu-${row.id}`}
              >
                <i className="fas fa-fw fa-ellipsis-vertical" aria-hidden="true" />
              </DropdownButton>
              <Headless.Portal>
                <DropdownMenu anchor="bottom end" className={menuPanelClasses}>
                  {/* the menu lists EVERY entry action; the title pencil is the shortcut
                      (same rule as the paperclip menu, which lists View beside the link) */}
                  <DropdownItem
                    onClick={onEdit}
                    data-testid={`${testIdPrefix}-menu-edit-${row.id}`}
                  >
                    <i data-slot="icon" className="fas fa-fw fa-pencil" />
                    {document ? `Edit ${section.label}` : `Edit ${section.label} / attach document`}
                  </DropdownItem>
                  <DropdownItem onClick={onDelete} data-testid={`${testIdPrefix}-delete-${row.id}`}>
                    <i
                      data-slot="icon"
                      className="fa-regular fa-fw fa-trash tw:!text-stoplight-danger"
                    />
                    <span className="tw:text-stoplight-danger">Remove {section.label}</span>
                  </DropdownItem>
                </DropdownMenu>
              </Headless.Portal>
            </Dropdown>
          )}
        </>
      }
      data-testid={`${testIdPrefix}-card-${row.id}`}
    />
  );
}

/** The add control of an entry section: the add-bar button, or the title bar's "Add one" link. */
function AddEntryControl({
  as,
  section,
  onClick,
  testIdPrefix,
  children,
}: {
  as: 'button' | 'link';
  section: CredentialSectionSpec;
  onClick: () => void;
  testIdPrefix: string;
  children: React.ReactNode;
}) {
  if (as === 'link') {
    return (
      <button
        type="button"
        className="tw:text-brand-green-0 tw:hover:underline tw:text-left"
        onClick={onClick}
        title={`Add ${section.label}`}
        data-testid={`${testIdPrefix}-add-link-${section.key}`}
      >
        {children}
      </button>
    );
  }
  return (
    <Button
      color="brand-green-outline"
      small
      onClick={onClick}
      title={`Add ${section.label}`}
      data-testid={`${testIdPrefix}-add-${section.key}`}
    >
      {children}
    </Button>
  );
}

type SectionItemContext = {
  maps: ProviderDrawerMaps | undefined;
  viewer: CardProps['viewer'];
  documentActions: CardProps['documentActions'];
  canUpdate: boolean;
  canDownload: (document: NonNullable<CredentialRow['document']>) => boolean;
  testIdPrefix: string;
  /** Open the entry form on `row` (null: a new entry of the section). */
  onEdit: (row: CredentialRow | null) => void;
  onDelete: (row: CredentialRow) => void;
};

/** One credential section as a `SectionItem`: its rows (of the kind, in the section), sorted. */
function sectionItem(
  section: CredentialSectionSpec,
  kindRows: CredentialRow[],
  context: SectionItemContext
): SectionItem {
  const { maps, viewer, documentActions, canUpdate, canDownload, testIdPrefix } = context;
  const rows = sortRows(kindRows.filter((row) => rowInSection(row, section)));
  const downloadableDocument = (row: CredentialRow) =>
    row.document && canDownload(row.document) ? row.document : null;
  return {
    key: section.key,
    title: section.title(rows.length),
    label: section.label,
    count: rows.length,
    required: section.required,
    canAdd: canUpdate,
    renderAdd: (as, content) => (
      <AddEntryControl
        as={as}
        section={section}
        onClick={() => context.onEdit(null)}
        testIdPrefix={testIdPrefix}
      >
        {content}
      </AddEntryControl>
    ),
    selectableIds: rows.flatMap((row) => {
      const document = downloadableDocument(row);
      return document ? [document.id] : [];
    }),
    renderCards: (selection: Selection) =>
      rows.map((row) => {
        const document = downloadableDocument(row);
        return (
          <CredentialEntryCard
            key={row.id}
            row={row}
            section={section}
            maps={maps}
            viewer={viewer}
            documentActions={documentActions}
            canUpdate={canUpdate}
            leading={document ? selection.renderCheckbox(document.id, row.title) : null}
            onEdit={() => context.onEdit(row)}
            onDelete={() => context.onDelete(row)}
            testIdPrefix={testIdPrefix}
          />
        );
      }),
  };
}

/**
 * Credential ENTRIES (licenses, board / life-support certifications, immunizations, the
 * ECFMG certificate) as `SectionItem`s for a tab's `SectionList`: the structured rows
 * of each section with ONE backing document each, their add control (the entry form),
 * the cards, and the dialogs the cards open (entry form, remove confirmation, document
 * actions). Loads one list per credential kind the sections span.
 */
export function useCredentialSectionItems({
  providerId,
  sections,
  maps,
  testIdPrefix,
}: {
  providerId: string;
  sections: readonly CredentialSectionSpec[];
  maps: ProviderDrawerMaps | undefined;
  testIdPrefix: string;
}): { items: SectionItem[]; isLoading: boolean; dialogs: React.ReactNode } {
  const queryClient = useQueryClient();
  const audience = useAudience();
  const canUpdate = useHasPermission(PROVIDER_UPDATE_PERMISSION);
  const canUpdateSignature = useHasPermission(SIGNATURE_REQUEST_UPDATE_PERMISSION);
  const canDownload = useCanDownloadDocument(WorkflowScopes.Provider);
  const viewer = { ownerId: providerId, audience, canUpdateSignature };
  const kinds = Array.from(new Set(sections.map((section) => section.kind)));

  const [editing, setEditing] = useState<{
    section: CredentialSectionSpec;
    row: CredentialRow | null;
  } | null>(null);
  const [deleting, setDeleting] = useState<{ kind: CredentialKind; row: CredentialRow } | null>(
    null
  );

  const queries = useQueries({
    queries: kinds.map((kind) => ({
      queryKey: credentialsQueryKey(providerId, kind),
      queryFn: () => credentialApi[kind].list(providerId),
      enabled: !!providerId,
    })),
  });
  const isLoading = queries.some((query) => query.isLoading);
  const rowsByKind = Object.fromEntries(
    kinds.map((kind, index) => [kind, queries[index]?.data ?? []])
  ) as Partial<Record<CredentialKind, CredentialRow[]>>;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_CREDENTIALS_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({
      queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
    });
  };
  const documentActions = useDocumentActions({ invalidate });

  const deleteMutation = useMutation({
    mutationFn: ({ kind, row }: { kind: CredentialKind; row: CredentialRow }) =>
      credentialApi[kind].destroy(row.id),
    onSuccess: (_, { row }) => {
      toast.success(`${row.title} removed`);
      invalidate();
      setDeleting(null);
    },
    onError: (error: Error) => handleApiError(error, { messagePrefix: 'Error removing entry' }),
  });

  const items = sections.map((section) =>
    sectionItem(section, rowsByKind[section.kind] ?? [], {
      maps,
      viewer,
      documentActions,
      canUpdate,
      canDownload,
      testIdPrefix,
      onEdit: (row) => setEditing({ section, row }),
      onDelete: (row) => setDeleting({ kind: section.kind, row }),
    })
  );

  const dialogs = (
    <>
      {editing && (
        <CredentialFormDialog
          // remount per target so the form seeds from the right entry / section
          key={`${editing.section.key}|${editing.row?.id ?? 'new'}`}
          open
          onClose={() => setEditing(null)}
          providerId={providerId}
          section={editing.section}
          row={editing.row}
        />
      )}
      <ConfirmationDialog
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) deleteMutation.mutate(deleting);
        }}
        title={`Remove ${deleting?.row.title ?? 'entry'}?`}
        body={
          deleting?.row.document
            ? 'The entry and its document are removed together. Replace the document from its paperclip menu instead if only the file is wrong.'
            : 'The entry is removed from the clinician’s credentials.'
        }
        confirmText="Remove"
        confirmVariant="danger"
        isLoading={deleteMutation.isPending}
      />
      <DocumentActionDialogs actions={documentActions} audience={audience} />
    </>
  );

  return { items, isLoading, dialogs };
}
